import type { StudioAvailability, WeeklyAvailability } from '@/api/types/ProviderApi'
import {
	addDays,
	addMonths,
	eachDayOfInterval,
	endOfMonth,
	format,
	parseISO,
	startOfMonth,
	startOfWeek,
} from 'date-fns'
import { Platform } from 'react-native'

/**
 * The booking picker as data: a week of openings, grouped by studio.
 *
 * Pure, with `now` injected, so every date boundary is testable without a clock. Nothing here
 * knows about React or Tailwind: classes written under `src/features` are never generated, since
 * the content globs cover `src/app`, `src/components` and `src/ui` only.
 *
 * Times are the studio's wall clock and are never converted. `startTime` arrives as "HH:mm:ss"
 * and is formatted by string arithmetic, so a patient in another timezone still reads the hour
 * they will turn up at.
 */

export const DAY_FORMAT = 'yyyy-MM-dd'

export type TimeChip = {
	/** Stable across refetches, so a chosen time can be found again. */
	key: string
	date: string
	startTime: string
	/** "9:00 AM" */
	label: string
	/** "2026-05-06T09:00:00", exactly what the booking call sends. */
	startTimeLocal: string
	locationId: number
	durationInMinutes: number
	podId: number
}

export type DayGroup = {
	date: string
	/** "Today" | "Tomorrow" | "Mon, May 6" */
	label: string
	chips: TimeChip[]
}

export type StudioSection = {
	locationId: number
	name: string
	isHomeStudio: boolean
	address: string
	structuredAddress: StudioAvailability['structuredAddress']
	/** A building or suite, where the street alone does not find the door. */
	siteName: string | null
	mapUrl: string
	/** Null where the studio has none; the sheet leaves its map out rather than guessing. */
	coordinates: { lat: number; lng: number } | null
	timezone: string
	hasAvailability: boolean
	/** Openings this week; the collapsed card says this much without being opened. */
	timeCount: number
	isExpanded: boolean
	/** Only days that have openings. Empty when the studio has nothing this week. */
	days: DayGroup[]
}

/** What the patient has opened or closed by hand; everything else follows the defaults. */
export type ExpansionState = {
	manuallyExpanded: number[]
	manuallyCollapsed: number[]
}

export const NO_EXPANSION: ExpansionState = {
	manuallyExpanded: [],
	manuallyCollapsed: [],
}

export type WeekView = {
	weekStart: string
	label: string
	canGoBack: boolean
	canGoForward: boolean
	studios: StudioSection[]
	/** No studio has anything this week. */
	isEmpty: boolean
	totalTimes: number
	/** Every studio shares one timezone and it is not the patient's own. */
	showsTimezoneNote: boolean
	timezoneLabel: string
}

export type CalendarCell = {
	date: string
	dayOfMonth: number
	inMonth: boolean
	isSelectable: boolean
	isInSelectedWeek: boolean
}

const toDay = (date: Date) => format(date, DAY_FORMAT)

/** Parsed as a plain calendar day, never as an instant, so no timezone can shift it. */
const dayToDate = (day: string) => parseISO(`${day}T00:00:00`)

export const weekStartOf = (day: string | Date) =>
	toDay(startOfWeek(typeof day === 'string' ? dayToDate(day) : day, { weekStartsOn: 1 }))

/** The seven days of a week, as `yyyy-MM-dd`, so a grid can ask each studio about each one. */
export const weekDays = (weekStart: string) =>
	Array.from({ length: 7 }, (_, index) => toDay(addDays(dayToDate(weekStart), index)))

export const addWeeks = (weekStart: string, count: number) =>
	toDay(addDays(dayToDate(weekStart), count * 7))

/**
 * Guards the wall clock the booking call sends.
 *
 * The server always writes seconds today, so this is a no-op; it costs a comparison and means a
 * serde default changing under us cannot quietly send a time the booking route reads differently.
 */
export const withSeconds = (startTime: string) =>
	startTime.split(':').length === 2 ? `${startTime}:00` : startTime

/** "9:00 AM" from "09:00" or "09:00:00", by string arithmetic: no Date, so no timezone. */
export const formatSlotTime = (startTime: string) => {
	const [rawHours = '0', rawMinutes = '00'] = startTime.split(':')
	const hours = Number.parseInt(rawHours, 10)
	const suffix = hours < 12 ? 'AM' : 'PM'
	const hour12 = hours % 12 === 0 ? 12 : hours % 12
	return `${hour12}:${rawMinutes} ${suffix}`
}

export const formatDayLabel = (day: string, now: Date) => {
	const today = toDay(now)
	if (day === today) {
		return 'Today'
	}
	if (day === toDay(addDays(now, 1))) {
		return 'Tomorrow'
	}
	return format(dayToDate(day), 'EEE, MMM d')
}

export const weekLabel = (weekStart: string, now: Date) => {
	const thisWeek = weekStartOf(now)
	if (weekStart === thisWeek) {
		return 'This week'
	}
	if (weekStart === addWeeks(thisWeek, 1)) {
		return 'Next week'
	}
	const start = dayToDate(weekStart)
	const end = addDays(start, 6)
	const endFormat = format(start, 'MMM') === format(end, 'MMM') ? 'd' : 'MMM d'
	return `${format(start, 'MMM d')} – ${format(end, endFormat)}`
}

/**
 * The link out to a map, in whichever map the phone actually has.
 *
 * It sent everyone to maps.google.com, which on an iPhone without the Google app is a website
 * asking to be installed — for a member standing on a corner trying to find a door. Apple's own
 * scheme opens Maps on iOS; Android's `geo:` opens whatever is set as the map there, which is
 * usually Google's and is in any case the one that phone has chosen.
 *
 * The name rides along so the pin is labelled rather than dropped on a bare coordinate.
 *
 * The platform is a parameter with the real one as its default, so both branches can be read
 * back in a test without standing up a mock of the whole module.
 */
export const mapUrlFor = (
	studio: Pick<StudioAvailability, 'name' | 'coordinates'>,
	platform: string = Platform.OS
) => {
	const label = encodeURIComponent(studio.name)
	const isIOS = platform === 'ios'

	if (!studio.coordinates) {
		return isIOS ? `http://maps.apple.com/?q=${label}` : `geo:0,0?q=${label}`
	}

	const { lat, lng } = studio.coordinates
	return isIOS
		? `http://maps.apple.com/?ll=${lat},${lng}&q=${label}`
		: `geo:${lat},${lng}?q=${lat},${lng}(${label})`
}

const daysOfWeek = (weekStart: string) =>
	Array.from({ length: 7 }, (_, index) => toDay(addDays(dayToDate(weekStart), index)))

const chipsFor = (studio: StudioAvailability, day: string): TimeChip[] =>
	[...(studio.slotsByDate[day] ?? [])]
		.sort((a, b) => a.startTime.localeCompare(b.startTime))
		.map((slot) => ({
			key: `${studio.locationId}|${day}|${slot.startTime}`,
			date: day,
			startTime: slot.startTime,
			label: formatSlotTime(slot.startTime),
			startTimeLocal: `${day}T${withSeconds(slot.startTime)}`,
			locationId: studio.locationId,
			durationInMinutes: slot.durationInMinutes,
			podId: slot.podId,
		}))

/**
 * A studio's week, with days that have already happened dropped.
 *
 * `now` was a parameter here and used only to write the label, so a week containing today kept
 * Monday's and Tuesday's slots as though they were bookable: the strip marked them open and the
 * screen offered them as somewhere else to go. A date in the past is not an alternative.
 *
 * Whole days only. A time earlier today is the server's call — it knows its own lead times and
 * how long before a slot it will still take a booking, and second-guessing that here would hide
 * openings it is willing to sell.
 */
const dayGroupsFor = (
	studio: StudioAvailability,
	weekStart: string,
	now: Date
): DayGroup[] => {
	const today = format(now, 'yyyy-MM-dd')
	return daysOfWeek(weekStart)
		.filter((day) => day >= today)
		.map((day) => ({
			date: day,
			label: formatDayLabel(day, now),
			chips: chipsFor(studio, day),
		}))
		.filter((group) => group.chips.length > 0)
}

/** The studio opened on arrival: the one being rescheduled from, else the patient's own. */
export const defaultExpanded = (
	availability?: WeeklyAvailability,
	preferLocationId?: number
): number[] => {
	const studios = availability?.locations ?? []
	const preferred = studios.find((studio) => studio.locationId === preferLocationId)
	const home = studios.find((studio) => studio.isHomeStudio)
	const chosen = preferred ?? home
	return chosen ? [chosen.locationId] : []
}

/**
 * Which studios are open, given the defaults and what the patient has done by hand.
 *
 * When everything open is empty this week, the first studio that has times opens itself, so a
 * dry home studio never looks like a dead end. A studio the patient closed stays closed even
 * then: their choice outranks the convenience.
 */
export const effectiveExpanded = (
	availability: WeeklyAvailability | undefined,
	expansion: ExpansionState,
	preferLocationId?: number
): Set<number> => {
	const studios = availability?.locations ?? []
	const collapsed = new Set(expansion.manuallyCollapsed)
	const open = new Set(
		[
			...defaultExpanded(availability, preferLocationId),
			...expansion.manuallyExpanded,
		].filter((locationId) => !collapsed.has(locationId))
	)

	const anyOpenHasTimes = studios.some(
		(studio) => open.has(studio.locationId) && studio.hasAvailability
	)
	if (!anyOpenHasTimes) {
		const rescue = studios.find(
			(studio) => studio.hasAvailability && !collapsed.has(studio.locationId)
		)
		if (rescue) {
			open.add(rescue.locationId)
		}
	}

	return open
}

export const buildWeekView = ({
	availability,
	weekStart,
	now,
	expansion,
	preferLocationId,
	deviceTimezone,
}: {
	availability?: WeeklyAvailability
	weekStart: string
	now: Date
	expansion: ExpansionState
	preferLocationId?: number
	deviceTimezone?: string
}): WeekView => {
	const locations = availability?.locations ?? []
	const open = effectiveExpanded(availability, expansion, preferLocationId)

	// Server order is kept, empty studios included: a list that reshuffles week to week is
	// harder to use than one where a studio is always in the same place.
	const studios: StudioSection[] = locations.map((studio) => {
		const days = dayGroupsFor(studio, weekStart, now)
		return {
			locationId: studio.locationId,
			name: studio.name,
			isHomeStudio: studio.isHomeStudio,
			address: studio.address,
			structuredAddress: studio.structuredAddress,
			siteName: studio.siteName,
			mapUrl: mapUrlFor(studio),
			coordinates: studio.coordinates,
			timezone: studio.timezone,
			hasAvailability: days.length > 0,
			timeCount: days.reduce((total, day) => total + day.chips.length, 0),
			isExpanded: open.has(studio.locationId),
			days,
		}
	})

	const zones = new Set(locations.map((studio) => studio.timezone))
	const onlyZone = zones.size === 1 ? [...zones][0] : undefined
	const totalTimes = studios.reduce((total, studio) => total + studio.timeCount, 0)

	return {
		weekStart,
		label: weekLabel(weekStart, now),
		canGoBack: availability ? weekStart > availability.minWeekStart : false,
		canGoForward: availability ? weekStart < availability.maxWeekStart : false,
		studios,
		isEmpty: totalTimes === 0,
		totalTimes,
		showsTimezoneNote: !!onlyZone && !!deviceTimezone && onlyZone !== deviceTimezone,
		timezoneLabel: onlyZone
			? (onlyZone.split('/')[1] ?? onlyZone).replace(/_/g, ' ')
			: '',
	}
}

/** How many openings a day holds across every studio, which is what marks a day worth tapping. */
export const countOnDay = (view: WeekView, date: string) =>
	view.studios.reduce(
		(total, studio) =>
			total + (studio.days.find((day) => day.date === date)?.chips.length ?? 0),
		0
	)

/**
 * The day a week opens on: the first one with anything in it.
 *
 * Derived rather than stored, so stepping to a new week lands on that week's soonest opening
 * without anything having to remember to reset. Undefined for a week with nothing at all, which
 * is the case the empty state answers.
 */
export const firstOpenDay = (view: WeekView) =>
	weekDays(view.weekStart).find((date) => countOnDay(view, date) > 0)

/** Whether the time a patient chose is still on offer after a refetch. */
export const chipStillExists = (view: WeekView, key: string) =>
	view.studios.some((studio) =>
		studio.days.some((day) => day.chips.some((chip) => chip.key === key))
	)

/**
 * The month grid behind the jump-to-a-date control.
 *
 * Always six rows, so the grid never changes height as months are paged. Days outside the
 * bookable window are drawn but not selectable. There are deliberately no availability marks:
 * only the weeks already fetched are known, so a blank day would mean two different things.
 */
export const monthGrid = (
	month: string,
	selectedWeekStart: string,
	bounds: { earliestBookableDate: string; maxBookableDate: string }
): CalendarCell[][] => {
	const first = startOfMonth(dayToDate(month))
	const gridStart = startOfWeek(first, { weekStartsOn: 1 })
	const lastOfMonth = toDay(endOfMonth(first))
	const cells = eachDayOfInterval({ start: gridStart, end: addDays(gridStart, 41) }).map(
		(date) => {
			const day = toDay(date)
			return {
				date: day,
				dayOfMonth: date.getDate(),
				inMonth: day >= toDay(first) && day <= lastOfMonth,
				isSelectable: day >= bounds.earliestBookableDate && day <= bounds.maxBookableDate,
				isInSelectedWeek: weekStartOf(day) === selectedWeekStart,
			}
		}
	)
	return Array.from({ length: 6 }, (_, row) => cells.slice(row * 7, row * 7 + 7))
}

export const monthOf = (day: string) => toDay(startOfMonth(dayToDate(day)))

export const shiftMonth = (month: string, count: number) =>
	toDay(startOfMonth(addMonths(dayToDate(month), count)))
